/* ============================================================
   acg-box-builder.js
   Free-shipping box builder (sections/acg-box-builder.liquid).

   The "box" is the real cart. Every card adds to / removes from the
   cart directly, and the sticky meter reads /cart.js to show:
     - box total vs the free-shipping minimum (data-min-cents)
     - box weight vs the weight cap (data-max-grams)

   The free shipping itself is applied at checkout by the delivery
   function in the acg-bundle-discount app. This script only shows
   progress; it never discounts anything.

   Cart sync:
     - After each add/remove it re-reads /cart.js, then asks the theme's
       cart drawer to refresh (badge + drawer contents) WITHOUT opening it.
     - Changes made elsewhere (cart drawer, free-gift script, another tab)
       are picked up by watching the header cart count, the acg:cart:*
       events, and pageshow/visibilitychange.
     - Lines carrying the hidden `_free_gift` property belong to
       acg-free-gift-auto.js and are never counted or touched here.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-acg-box]');
  if (!root || root._acgBoxInit) {
    return;
  }
  root._acgBoxInit = true;

  var MIN_CENTS = parseInt(root.getAttribute('data-min-cents'), 10) || 5000;
  var MAX_GRAMS = parseInt(root.getAttribute('data-max-grams'), 10) || 9072;
  var GRAMS_PER_LB = 453.592;
  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ERROR_CLEAR_MS = 5000;

  var cart = null;
  var cards = Array.prototype.slice.call(root.querySelectorAll('[data-box-card]'));

  /* ---- Formatting ------------------------------------------------- */

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function pounds(grams) {
    // One decimal everywhere, so "18.2 lb" and "room for 1.8 lb" add up.
    return Math.round((grams / GRAMS_PER_LB) * 10) / 10 + ' lb';
  }

  /* ---- Cart I/O ---------------------------------------------------- */

  function request(path, body) {
    return fetch(ROOT_URL + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || (data && data.status && data.status !== 200)) {
          throw new Error((data && (data.description || data.message)) ||
            'Sorry, that could not be updated. Please try again.');
        }
        return data;
      });
    });
  }

  var syncInFlight = null;
  function sync() {
    if (syncInFlight) {
      return syncInFlight;
    }
    syncInFlight = request('cart.js')
      .then(function (data) {
        cart = data;
        render();
        return data;
      })
      .catch(function () { /* keep the last known state */ })
      .then(function (data) {
        syncInFlight = null;
        return data;
      });
    return syncInFlight;
  }

  // Tell the rest of the theme the cart changed, without opening the drawer.
  function notifyTheme() {
    if (window.ACGCartDrawer && typeof window.ACGCartDrawer.refresh === 'function') {
      window.ACGCartDrawer.refresh();
    }
    if (window.ACGAjaxCart && typeof window.ACGAjaxCart.refreshCartCount === 'function') {
      window.ACGAjaxCart.refreshCartCount();
    }
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties._free_gift);
  }

  function quantityOf(variantId) {
    if (!cart) {
      return 0;
    }
    var total = 0;
    cart.items.forEach(function (item) {
      if (String(item.variant_id) === String(variantId) && !isGiftLine(item)) {
        total += item.quantity;
      }
    });
    return total;
  }

  // The line to decrement: the last non-gift line for this variant.
  function lineOf(variantId) {
    if (!cart) {
      return null;
    }
    var found = null;
    cart.items.forEach(function (item) {
      if (String(item.variant_id) === String(variantId) && !isGiftLine(item)) {
        found = item;
      }
    });
    return found;
  }

  /* ---- Cards ------------------------------------------------------- */

  function selectedOption(card) {
    var select = card.querySelector('[data-box-variant]');
    return select ? select.options[select.selectedIndex] : null;
  }

  function buildPills(card) {
    var holder = card.querySelector('[data-box-pills]');
    var select = card.querySelector('[data-box-variant]');
    if (!holder || !select) {
      return;
    }
    Array.prototype.forEach.call(select.options, function (opt) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'acg-box__pill';
      pill.setAttribute('role', 'radio');
      pill.setAttribute('data-value', opt.value);
      pill.textContent = opt.getAttribute('data-label');
      pill.addEventListener('click', function () {
        select.value = opt.value;
        renderCard(card);
      });
      holder.appendChild(pill);
    });
  }

  function setBusy(card, busy) {
    card._busy = busy;
    card.classList.toggle('is-busy', busy);
    Array.prototype.forEach.call(card.querySelectorAll('button'), function (b) {
      b.disabled = busy;
    });
  }

  function showError(card, message) {
    var el = card.querySelector('[data-box-err]');
    if (!el) {
      return;
    }
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(function () {
      el.hidden = true;
      el.textContent = '';
    }, ERROR_CLEAR_MS);
  }

  function renderCard(card) {
    var opt = selectedOption(card);
    if (!opt) {
      return;
    }
    var price = parseInt(opt.getAttribute('data-price'), 10) || 0;
    var compare = parseInt(opt.getAttribute('data-compare'), 10) || 0;
    var grams = parseInt(opt.getAttribute('data-grams'), 10) || 0;

    card.querySelector('[data-box-price]').textContent = money(price);
    var compareEl = card.querySelector('[data-box-compare]');
    compareEl.hidden = !(compare > price);
    compareEl.textContent = compare > price ? money(compare) : '';
    card.querySelector('[data-box-weight]').textContent = grams > 0 ? '≈ ' + pounds(grams) : '';

    Array.prototype.forEach.call(card.querySelectorAll('.acg-box__pill'), function (pill) {
      var on = pill.getAttribute('data-value') === opt.value;
      pill.classList.toggle('is-active', on);
      pill.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var qty = quantityOf(opt.value);
    card.querySelector('[data-box-add]').hidden = qty > 0;
    card.querySelector('[data-box-stepper]').hidden = qty === 0;
    card.querySelector('[data-box-qty]').textContent = qty;
    card.classList.toggle('is-in-box', qty > 0);
  }

  function addOne(card) {
    var opt = selectedOption(card);
    if (!opt || card._busy) {
      return;
    }
    setBusy(card, true);
    request('cart/add.js', { items: [{ id: parseInt(opt.value, 10), quantity: 1 }] })
      .then(sync)
      .then(notifyTheme)
      .catch(function (err) { showError(card, err.message); })
      .then(function () { setBusy(card, false); renderCard(card); });
  }

  function removeOne(card) {
    var opt = selectedOption(card);
    var line = opt ? lineOf(opt.value) : null;
    if (!line || card._busy) {
      return;
    }
    setBusy(card, true);
    request('cart/change.js', { id: line.key, quantity: line.quantity - 1 })
      .then(function (data) {
        cart = data;
        render();
      })
      .then(notifyTheme)
      .catch(function (err) { showError(card, err.message); })
      .then(function () { setBusy(card, false); renderCard(card); });
  }

  /* ---- Meter ------------------------------------------------------- */

  var els = {
    total: root.querySelector('[data-box-total]'),
    lb: root.querySelector('[data-box-lb]'),
    fillMoney: root.querySelector('[data-box-fill-money]'),
    fillWeight: root.querySelector('[data-box-fill-weight]'),
    status: root.querySelector('[data-box-status]')
  };

  function renderMeter() {
    var cents = cart ? cart.items_subtotal_price : 0;
    var grams = cart ? cart.total_weight : 0;
    var count = cart ? cart.item_count : 0;
    var over = grams > MAX_GRAMS;
    var reached = cents >= MIN_CENTS;
    var free = reached && !over && count > 0;

    els.total.textContent = money(cents);
    els.lb.textContent = pounds(grams);
    els.fillMoney.style.width = Math.min(100, (cents / MIN_CENTS) * 100) + '%';
    els.fillWeight.style.width = Math.min(100, (grams / MAX_GRAMS) * 100) + '%';

    var msg;
    if (count === 0) {
      msg = 'Add ' + money(MIN_CENTS).replace('.00', '') + ' of products to ship free.';
    } else if (over) {
      msg = 'Your box is ' + pounds(grams) + '. Take out ' + pounds(grams - MAX_GRAMS) +
        ' to keep shipping free.';
    } else if (!reached) {
      msg = 'Add ' + money(MIN_CENTS - cents) + ' more to ship free.';
    } else {
      msg = 'Your box ships free. Room for ' + pounds(MAX_GRAMS - grams) + ' more.';
    }
    if (els.status.textContent !== msg) {
      els.status.textContent = msg;
    }

    root.classList.toggle('is-free', free);
    root.classList.toggle('is-over', over);
    root.classList.toggle('is-reached', reached);
    root.classList.toggle('has-items', count > 0);
  }

  function render() {
    renderMeter();
    cards.forEach(renderCard);
  }

  /* ---- Wiring ------------------------------------------------------ */

  cards.forEach(function (card) {
    buildPills(card);
    var select = card.querySelector('[data-box-variant]');
    if (select) {
      select.addEventListener('change', function () { renderCard(card); });
    }
    card.querySelector('[data-box-add]').addEventListener('click', function () { addOne(card); });
    card.querySelector('[data-box-inc]').addEventListener('click', function () { addOne(card); });
    card.querySelector('[data-box-dec]').addEventListener('click', function () { removeOne(card); });
    renderCard(card);
  });

  var review = root.querySelector('[data-box-review]');
  if (review) {
    review.addEventListener('click', function () {
      if (window.ACGCartDrawer && typeof window.ACGCartDrawer.open === 'function') {
        window.ACGCartDrawer.open();
      } else {
        window.location.href = ROOT_URL + 'cart';
      }
    });
  }

  // Pick up cart changes made outside the builder.
  var resyncTimer = null;
  function resyncSoon() {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(sync, 300);
  }
  document.addEventListener('acg:cart:added', resyncSoon);
  document.addEventListener('acg:cart:updated', resyncSoon);
  window.addEventListener('pageshow', resyncSoon);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      resyncSoon();
    }
  });
  if (window.MutationObserver) {
    var observer = new MutationObserver(resyncSoon);
    Array.prototype.forEach.call(document.querySelectorAll('.acg-nav__cart-count'), function (el) {
      observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true });
    });
  }

  sync();
}());
